import {
  ArticleWithRelations,
  isOnlyArticleInPaymentStep,
  isOnlyArticleInRefundtStep,
  RequestWithRelations,
} from '@/models/request';
import {
  canActOnBehalfOfNonDigitalizedWorkshop,
  getScopesForTraits,
  hasPermission,
} from '@/models/user';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { AcceptDispatchProposalAction } from '@/routes/Requests/Request/Article/components/ArticleActions/AcceptDispatchProposalAction';
import { useCurrentSession } from '@/services/auth';

import { ValidatePaymentAction } from '../../../components/RequestActionsFooter/components/ValidatePaymentAction/ValidatePaymentAction';
import { ValidateRefundAction } from '../../../components/RequestActionsFooter/components/ValidateRefundAction';

import { AcceptDispatchAction } from './AcceptDispatchAction';
import { AcceptRequalificationAction } from './AcceptRequalificationAction';
import { AddCustomsInformationsAction } from './AddCustomsInformationsAction';
import { AdjustServiceDuringProcessingAction } from './AdjustServiceDuringProcessingAction';
import { AdjustServiceDuringQualityControlAction } from './AdjustServiceDuringQualityControlAction';
import { AnalyzeArticleAction } from './AnalyzeArticleAction';
import { ChooseArticleServiceAction } from './ChooseArticleServiceAction';
import { CompleteClientPickup } from './CompleteClientPickup';
import { CompleteSelfHandledTransitAction } from './CompleteSelfHandledTransitAction';
import { ConfirmExchangeStockAvailabilityAction } from './ConfirmExchangeStockAvailabilityAction';
import { ContactClientAction } from './ContactClientAction';
import { ControlQualityAction } from './ControlQualityAction';
import { CreateShipmentAction } from './CreateShipmentAction';
import { EchangeArticleAction } from './ExchangeArticleAction';
import { HandleShipmentIssueAction } from './HandleShipmentIssueAction';
import { ProcessingAction } from './ProcessingAction';
import { RepairArticleAction } from './RepairArticleAction';
import { StartArticleExchangeAction } from './StartArticleExchangeAction';
import { StartArticleRepairAction } from './StartArticleRepairAction';
import { TransitFromNonDigitalizedWorkshopToStoreAction } from './TransitFromNonDigitalizedWorkshopToStoreAction';
import { ValidateOnBehalfOfClientAction } from './ValidateOnBehalfOfClientAction';
import { ValidateTransitAction } from './ValidateTransitAction';

export type ArticleActionProps = {
  article: ArticleWithRelations;
  request: RequestWithRelations;
  onActionDone: (options?: { navigateToRequests?: boolean }) => void;
};

const ArticleActions = (props: ArticleActionProps) => {
  const { currentSession } = useCurrentSession();
  const { article, request } = props;
  const { workflow, iam } = useRequestContext();

  if (article.archivedAt) {
    return null;
  }

  if (article.taskType) {
    switch (article.taskType) {
      case 'choose_article_service':
        return <ChooseArticleServiceAction {...props} />;
      case 'accept_dispatch':
        return <AcceptDispatchAction {...props} />;
      case 'add_customs_informations':
        return <AddCustomsInformationsAction {...props} />;
      case 'adjust_service_during_quality_control':
        return <AdjustServiceDuringQualityControlAction {...props} />;
      case 'adjust_service_during_processing':
        return <AdjustServiceDuringProcessingAction {...props} />;
      case 'confirm_exchange_stock_availability':
        return <ConfirmExchangeStockAvailabilityAction {...props} />;
      case 'contact_client':
        return <ContactClientAction {...props} />;
      case 'create_shipment':
        return <CreateShipmentAction {...props} />;
      case 'validate_transit':
        return <ValidateTransitAction {...props} />;
      case 'handle_shipment_issue':
        return <HandleShipmentIssueAction {...props} />;
      case 'accept_requalification':
        return <AcceptRequalificationAction {...props} />;
      case 'analyze_article':
        return <AnalyzeArticleAction {...props} />;
      case 'start_article_exchange':
        return <StartArticleExchangeAction {...props} />;
      case 'start_article_repair':
        return <StartArticleRepairAction {...props} />;
      case 'repair_article':
        return <RepairArticleAction {...props} />;
      case 'control_quality':
        return <ControlQualityAction {...props} />;
      case 'accept_dispatch_proposal':
        return <AcceptDispatchProposalAction {...props} />;
      case 'exchange_article':
        return <EchangeArticleAction {...props} />;
      case 'validate_payment':
        if (isOnlyArticleInPaymentStep(request, article.id)) {
          return <ValidatePaymentAction {...props} />;
        } else {
          return null;
        }
      case 'validate_refund':
        if (isOnlyArticleInRefundtStep(request, article.id)) {
          return <ValidateRefundAction {...props} />;
        } else {
          return null;
        }
      default:
        return null;
    }
  }

  const isPendingValidationOnBehalfOfClient =
    article.step?.step === 'validation' && article.step.config.actOnBehalfOfClient;

  const canValidateOnBehalfOfClient = hasPermission(currentSession, 'write_client_communication', {
    entityId: request.storeId,
  });

  if (isPendingValidationOnBehalfOfClient && canValidateOnBehalfOfClient) {
    return <ValidateOnBehalfOfClientAction {...props} />;
  }

  const isPendingExchangeStockAvailabilityConfirmation =
    article.step?.step === 'service-choice' &&
    article.snapshot.serviceChoice === 'exchange' &&
    !!article.serviceChosenAt &&
    !article.exchangeStockAvailabilityConfirmedAt;

  if (isPendingExchangeStockAvailabilityConfirmation) {
    const chooseArticleServiceTraits =
      (article.step?.step === 'service-choice' &&
        article.step?.config?.tasks?.choose_article_service?.traits) ||
      [];

    const canConfirmExchangeStockAvailabilityOnBehalf = hasPermission(
      currentSession,
      'choose_article_service',
      getScopesForTraits({
        traits: chooseArticleServiceTraits,
        request,
        article,
      })
    );

    if (canConfirmExchangeStockAvailabilityOnBehalf) {
      return <ConfirmExchangeStockAvailabilityAction {...props} />;
    } else {
      return null;
    }
  }

  const isPendingClientPickup =
    article.step?.step === 'transit' &&
    !!request.storeId &&
    article.atEntityId === request.storeId &&
    article.toClient &&
    article.step?.config.clientPickup;

  if (isPendingClientPickup && iam.hasUserScopeOnRequest('store')) {
    return <CompleteClientPickup {...props} />;
  }

  const canActOnBehalfOfWorkshop = canActOnBehalfOfNonDigitalizedWorkshop({
    user: currentSession,
    request,
    workflow,
  });

  const canVerifyReceptionOnStore = hasPermission(currentSession, 'verify_shipment_reception', {
    entityId: request.storeId,
  });

  const isInProcessingStep = article.step?.step === 'processing';

  if ((canActOnBehalfOfWorkshop || canVerifyReceptionOnStore) && isInProcessingStep) {
    return <ProcessingAction {...props} />;
  }

  const isInTransitFromNonDigitalizedWorkshopToStore =
    article.step?.step === 'transit' &&
    !!article.atEntity?.workshopConfig?.nonDigitalized &&
    article.toEntityId === request.storeId;

  if (
    (canActOnBehalfOfWorkshop || canVerifyReceptionOnStore) &&
    isInTransitFromNonDigitalizedWorkshopToStore
  ) {
    return <TransitFromNonDigitalizedWorkshopToStoreAction {...props} />;
  }

  const isInSelfHandledTransit =
    article.selfHandledTransit?.enabled && article.selfHandledTransit?.started && article.inTransit;

  const canVerifyReception = hasPermission(currentSession, 'verify_shipment_reception', [
    {
      entityId: article.toEntityId,
    },
  ]);

  if (isInSelfHandledTransit && canVerifyReception) {
    return <CompleteSelfHandledTransitAction {...props} />;
  }

  if (isOnlyArticleInPaymentStep(request, article.id)) {
    return <ValidatePaymentAction {...props} />;
  }

  if (isOnlyArticleInRefundtStep(request, article.id)) {
    return <ValidateRefundAction {...props} />;
  }
};

export default ArticleActions;
